import { http, setAuthToken, clearAuthToken, getAuthToken } from './client'
import { API_BASE } from '../platform'
import { isDemoEmail } from '../demo'
import type { LoginResult, PublicUser, ReservationListResult } from './types'

/**
 * Login AND registration in one call: POST /security/login { username, password }.
 * An unknown username auto-registers (result.isNew === true). Must be sent WITHOUT
 * the mobile-token header (noAuth) or the firewall short-circuits the response.
 * Returns a JWT in result.token which we persist and replay as the mobile-token header.
 */
export async function login(username: string, password: string): Promise<LoginResult> {
  const res = await http.post<LoginResult>('/security/login', { username, password }, { noAuth: true })
  if (res?.loginResult && res.token) setAuthToken(res.token)
  return res
}

/**
 * Social sign-in — same /security/login endpoint, but with a provider token instead of a password.
 * The backend's LoginAuthenticator routes `google_token`/`fb_token` to SocialLoginService, which
 * verifies it (Google ID-token / Facebook Graph) and creates or links the member.
 */
export async function socialLogin(payload: { google_token?: string; fb_token?: string }): Promise<LoginResult> {
  const res = await http.post<LoginResult>('/security/login', payload, { noAuth: true })
  if (res?.loginResult && res.token) setAuthToken(res.token)
  return res
}

/** True when the email already has an (enabled) account — used to label login vs. register. */
export async function checkUsername(username: string): Promise<boolean> {
  if (isDemoEmail(username)) return true // demo accounts always "exist" → show the login step
  try {
    return await http.post<boolean>('/security/checkUsername', { username }, { noAuth: true })
  } catch {
    return false
  }
}

/** Sends the password-reset email. Always resolves true (no account enumeration). */
export async function rememberPassword(username: string): Promise<boolean> {
  return http.post<boolean>('/security/rememberPassword', { username }, { noAuth: true })
}

/**
 * Full-profile registration. Creates a pending account (formLoginEnabled=false) and sends the
 * activation email — the user sets the password via that link, which enables login. Does NOT log in.
 * Returns { ok, reason } — reason 'exists' when an active account already uses the email.
 */
export async function register(data: Record<string, unknown>): Promise<{ ok: boolean; reason?: string }> {
  return http.post<{ ok: boolean; reason?: string }>('/security/register', data, { noAuth: true })
}

/** Current member from the stored token (null if not authenticated / token invalid). */
export async function getCurrentUser(): Promise<PublicUser | null> {
  if (!getAuthToken()) return null
  try {
    return await http.get<PublicUser | null>('/security/user')
  } catch {
    return null
  }
}

/** Update the member profile (requires auth). */
export async function updateProfile(patch: Partial<PublicUser>): Promise<PublicUser | null> {
  return http.post<PublicUser | null>('/security/updateProfile', patch)
}

/**
 * Account deletion (App Store 5.1.1(v) / Google Play / Facebook). Asks the backend to remove the
 * member's personal data (name, email, phone, plate) and disable the account; past bookings are
 * anonymized + retained for accounting per law. Clears the local session on success.
 * Throws if the backend endpoint isn't available yet (the UI then offers the email-request path).
 */
export async function deleteAccount(): Promise<void> {
  await http.post<unknown>('/security/deleteAccount', {})
  clearAuthToken()
}

/** Clear the token and best-effort kill the server session/remember-me cookie. */
export async function logout(): Promise<void> {
  clearAuthToken()
  try {
    await fetch(`${API_BASE}/security/logout`, { credentials: 'include' })
  } catch {
    /* ignore */
  }
}

/** The member's bookings, split active (departure in the future) vs archived. */
export async function getReservationList(): Promise<ReservationListResult> {
  const data = await http.get<ReservationListResult | unknown[]>('/api/reservationList/')
  // backend returns an empty array (not an object) when unauthenticated
  if (Array.isArray(data)) return { activeList: [], archivedList: [] }
  return data as ReservationListResult
}
